import type { ArticleModule } from 'types/capi/article';

import type { ModuleDisplayType } from '~/events/ModuleImpressionEvent';

export type ModuleImpressionProps = {
  displayType?: ModuleDisplayType;
  itemCount?: number;
};

// Whether the article renders anything for this module. An empty content block,
// a product list with no items, an image grid with no items, an embed with
// nothing to embed and a row with no columns produce no element — the
// conditions their renderers return null on, mirrored here — so they get no
// impression, no place in the article view's module manifest and no ordinal
// from getModulePositions: the one predicate all three read, so what was
// counted, what was listed and what can be seen never drift apart.
export function rendersModule(module: ArticleModule): boolean {
  switch (module.type) {
    case 'content-block':
      return !!module.content?.length;
    case 'embed':
      return module.site === 'facebook' ? !!module.url : !!module.html;
    case 'image-grid':
      return !!module.items?.length;
    case 'module-row':
      return !!module.columns?.length;
    case 'product-list':
      return !!module.items?.some((item) => item != null);
    default:
      return true;
  }
}

export function getModuleImpressionProps(
  module: ArticleModule,
  { embedded = false }: { embedded?: boolean } = {}
): ModuleImpressionProps {
  switch (module.type) {
    case 'product-list': {
      const itemCount = module.items.filter((item) => item != null).length;

      return {
        displayType: module.display_type,
        itemCount:
          module.display_type === 'block' ? Math.min(itemCount, 1) : itemCount
      };
    }
    case 'image-grid':
      if (!embedded && module.display_type === 'full-bleed') {
        return {
          displayType: 'full-bleed',
          itemCount: Math.min(module.items.length, 1)
        };
      }

      return { displayType: 'grid', itemCount: module.items.length };
    default:
      return {};
  }
}
